import React, { useCallback, useEffect, useRef, useState } from 'react';
import { analystApi } from 'api';
import './CreativeLibrary.css';

const prettyBytes = (n) => {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const brandName = (b) => (typeof b === 'string' ? b : b?.name || b?.brand || '');

export default function CreativeLibrary() {
  const [brands, setBrands] = useState([]);
  const [brand, setBrand] = useState('');
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await analystApi.getBrands();
        const list = (data?.brands || data || []).map(brandName).filter(Boolean);
        if (!alive) return;
        setBrands(list);
        setBrand((b) => b || list[0] || '');
      } catch (_e) {
        if (alive) setError('No pude cargar las marcas.');
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const loadAssets = useCallback(async (b) => {
    if (!b) return;
    setLoading(true);
    setError(null);
    try {
      const data = await analystApi.listCreatives(b);
      setAssets(data?.assets || []);
    } catch (_e) {
      setError('No pude cargar la biblioteca.');
      setAssets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAssets(brand);
    setResults([]);
  }, [brand, loadAssets]);

  const upload = useCallback(
    async (files) => {
      if (!brand || !files?.length) return;
      setUploading(true);
      setError(null);
      try {
        const data = await analystApi.uploadCreatives(brand, files);
        setResults(data?.results || []);
        await loadAssets(brand);
      } catch (e) {
        setError(e.message || 'No se pudo subir el material.');
      } finally {
        setUploading(false);
      }
    },
    [brand, loadAssets]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer?.files?.length) upload(e.dataTransfer.files);
  };

  return (
    <div className="cl-wrap">
      <div className="cl-head">
        <h1>Biblioteca de creativos</h1>
        <label className="cl-brand">
          Marca
          <select value={brand} onChange={(e) => setBrand(e.target.value)}>
            {brands.length === 0 && <option value="">—</option>}
            {brands.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div
        className={`cl-drop ${dragging ? 'drag' : ''} ${uploading ? 'busy' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          hidden
          onChange={(e) => {
            if (e.target.files?.length) upload(e.target.files);
            e.target.value = '';
          }}
        />
        {uploading ? (
          <span>Subiendo…</span>
        ) : (
          <span>
            Arrastrá imágenes acá o <b>elegí archivos</b> — JPG, PNG o WEBP
          </span>
        )}
      </div>

      {error && <div className="cl-error">{error}</div>}

      {results.length > 0 && (
        <ul className="cl-results">
          {results.map((r, i) => (
            <li key={i} className={r.ok ? (r.duplicate ? 'dup' : 'ok') : 'bad'}>
              {r.ok
                ? r.duplicate
                  ? `Ya estaba en la biblioteca: ${r.asset?.filename || ''}`
                  : `Agregado: ${r.asset?.filename || ''}`
                : r.error}
            </li>
          ))}
        </ul>
      )}

      {loading ? (
        <div className="cl-empty">Cargando…</div>
      ) : assets.length === 0 ? (
        <div className="cl-empty">Todavía no hay material para esta marca.</div>
      ) : (
        <div className="cl-grid">
          {assets.map((a) => (
            <div className="cl-card" key={a.asset_id}>
              <div className="cl-thumb">
                {a.kind === 'image' ? (
                  <img src={a.url} alt={a.filename} loading="lazy" />
                ) : (
                  <span className="cl-video">▶</span>
                )}
              </div>
              <div className="cl-meta">
                <div className="cl-name" title={a.filename}>
                  {a.filename}
                </div>
                <div className="cl-badges">
                  {a.aspect_ratio && <span className="cl-badge">{a.aspect_ratio}</span>}
                  {a.width && a.height && (
                    <span className="cl-badge">
                      {a.width}×{a.height}
                    </span>
                  )}
                  <span className="cl-badge">{prettyBytes(a.byte_size)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
